import { useEffect, useRef } from "preact/hooks";

/**
 * Animated pipeline graph. Six nodes:
 *   intake → design → harden → audit → attest → deploy
 * Each node has a state: pending | running | done | error.
 * The component draws an SVG whose edges glow when their downstream node
 * transitions to `running`, and whose nodes pulse while running. State is
 * push-controlled from the parent (driven by the real pipeline running);
 * this component does no work other than rendering and animating.
 */
export type PipelineStage = "intake" | "design" | "harden" | "audit" | "attest" | "deploy";
export type StageState = "pending" | "running" | "done" | "error";

export interface PipelineState {
  stage: Record<PipelineStage, StageState>;
  /** Optional facts shown next to each completed node. Updated by the parent. */
  facts: Partial<Record<PipelineStage, string>>;
}

const STAGES: { id: PipelineStage; label: string; x: number; y: number }[] = [
  { id: "intake",  label: "intake",  x:  80, y: 120 },
  { id: "design",  label: "design",  x: 220, y: 120 },
  { id: "harden",  label: "harden",  x: 360, y: 120 },
  { id: "audit",   label: "audit",   x: 500, y: 120 },
  { id: "attest",  label: "attest",  x: 640, y: 120 },
  { id: "deploy",  label: "deploy",  x: 780, y: 120 }
];

const EDGES: [PipelineStage, PipelineStage][] = [
  ["intake", "design"],
  ["design", "harden"],
  ["harden", "audit"],
  ["audit", "attest"],
  ["attest", "deploy"]
];

function nodeColor(s: StageState): { fill: string; stroke: string; text: string } {
  switch (s) {
    case "done":    return { fill: "#1a2a1a", stroke: "#7ad48c", text: "#cfe9d4" };
    case "running": return { fill: "#2a2510", stroke: "#e9c46a", text: "#f2e1b3" };
    case "error":   return { fill: "#2a1414", stroke: "#e57a7a", text: "#f0c0c0" };
    default:        return { fill: "#0e0e0e", stroke: "#333", text: "#666" };
  }
}

export function PipelineCanvas(props: { state: PipelineState }) {
  const svgRef = useRef<SVGSVGElement>(null);
  // Repaint via a manual tick so the running-pulse can animate without
  // re-rendering React.
  useEffect(() => {
    const tick = (): void => {
      if (svgRef.current) {
        const t = Date.now() / 1000;
        const pulses = svgRef.current.querySelectorAll<SVGCircleElement>("[data-running]");
        pulses.forEach((c) => {
          const r = 28 + Math.sin(t * 4) * 2.5;
          c.setAttribute("r", String(r));
        });
      }
      raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 860 240"
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ maxWidth: "100%", display: "block", marginInline: "auto" }}
      aria-label="MCP build pipeline"
      role="img"
    >
      <defs>
        <linearGradient id="edge-active" x1="0" x2="1">
          <stop offset="0%" stop-color="#7ad48c" stop-opacity="0.0" />
          <stop offset="50%" stop-color="#e9c46a" stop-opacity="1" />
          <stop offset="100%" stop-color="#7ad48c" stop-opacity="0.0" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="2" />
          </feComponentTransfer>
        </filter>
      </defs>

      {/* edges */}
      {EDGES.map(([from, to]) => {
        const a = STAGES.find((s) => s.id === from)!;
        const b = STAGES.find((s) => s.id === to)!;
        const active = props.state.stage[from] === "done" && props.state.stage[to] !== "pending";
        const done = props.state.stage[from] === "done" && props.state.stage[to] === "done";
        return (
          <g key={`${from}-${to}`}>
            <line
              x1={a.x + 28}
              y1={a.y}
              x2={b.x - 28}
              y2={b.y}
              stroke={done ? "#7ad48c" : active ? "url(#edge-active)" : "#222"}
              stroke-width={2}
              stroke-linecap="round"
            />
            {active && !done && (
              <circle r={3} fill="#e9c46a" filter="url(#glow)">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(() => {
                  const props = { dur: "1.4s", repeatCount: "indefinite", path: `M ${a.x + 28} ${a.y} L ${b.x - 28} ${b.y}` } as Record<string, string>;
                  // animateMotion's `path` attr isn't typed; pass via spread to satisfy TS.
                  return <animateMotion {...props} />;
                })()}
              </circle>
            )}
          </g>
        );
      })}

      {/* nodes */}
      {STAGES.map((s) => {
        const st = props.state.stage[s.id];
        const c = nodeColor(st);
        return (
          <g key={s.id}>
            {st === "running" && (
              <circle cx={s.x} cy={s.y} r={28} fill="none" stroke={c.stroke} stroke-opacity={0.3} stroke-width={2} data-running />
            )}
            <circle cx={s.x} cy={s.y} r={24} fill={c.fill} stroke={c.stroke} stroke-width={st === "done" ? 2 : 1.5} />
            {st === "done" && (
              <path d={`M ${s.x - 7} ${s.y + 1} l 5 5 l 10 -11`} fill="none" stroke={c.stroke} stroke-width={2.5} stroke-linecap="round" stroke-linejoin="round" />
            )}
            {st === "running" && (
              <circle cx={s.x} cy={s.y} r={4} fill={c.stroke}>
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1s" repeatCount="indefinite" />
              </circle>
            )}
            {st === "error" && (
              <text x={s.x} y={s.y + 5} text-anchor="middle" font-size="18" fill={c.stroke} font-weight="bold">!</text>
            )}
            <text x={s.x} y={s.y + 50} text-anchor="middle" font-size="12" fill={c.text} font-family="monospace" letter-spacing="1">
              {s.label.toUpperCase()}
            </text>
            {props.state.facts[s.id] && (
              <text x={s.x} y={s.y + 68} text-anchor="middle" font-size="10" fill="#888" font-family="monospace">
                {props.state.facts[s.id]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function emptyPipelineState(): PipelineState {
  return {
    stage: {
      intake: "pending",
      design: "pending",
      harden: "pending",
      audit: "pending",
      attest: "pending",
      deploy: "pending"
    },
    facts: {}
  };
}
